/**
 * eslint-disable @typescript-eslint/naming-convention
 *
 * @format
 */

import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnalyticsService, Logger, ProductCatalogService } from '@shared/data-access/services';
import {
	CriteoPinnedContentSpot,
	CriteoViewProductsResponse,
	HeaderStoreSelection,
	PlpInGridSponsoredProduct,
	ProductAvailabilityCollection,
	ProductListSEO,
	ProductPromotionCollection,
	ProductPromotionResponse,
	ProductTypes,
	SEOAttributes
} from '@shared/data-access/interfaces';
import { DEFAULT_PAGE_SIZE, plpDefaultSortMap } from '@plp/data-access/constants';
import {
	DisplayableProductListData,
	DisplayableProductListParams,
	FallbackSearchParams,
	ProductDetail,
	ProductList,
	ProductListCore,
	ProductVO,
	RefinementUpdateObj,
	SearchVO
} from '@plp/data-access/interfaces';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
	ProductListApiService,
	ProductListMlService,
	ProductListSnackbarService,
	ProductListUIService
} from '@plp/data-access/services';
import { catchError, map, skipWhile, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	refreshProductList,
	removeAllFilters,
	setAllFilters,
	setCriteoProducts,
	setDisplayableProductListData,
	setFiltering,
	setIsProductListPageLoading,
	setPageNumber,
	setPageSizeAndPageNumber,
	setPinnedContent,
	setPiqProducts,
	setProductAvailability,
	setProductList,
	setProductListPromo,
	setRefinementUpdateObj,
	setSelectedSort,
	setSkeletonLoader,
	updateSelectedSort
} from './product-list.action';
import {
	selectCriteoProducts,
	selectFallbackSearchParams,
	selectFeatureFlags,
	selectPIQProducts,
	selectPageNumber,
	selectPageSize,
	selectPinnedContent,
	selectProductDetails,
	selectProductListCore,
	selectProductListSEO,
	selectProductListSEOAttributes,
	selectProductVOs,
	selectRefinementUpdateObj,
	selectSearchVO,
	selectSelectedFilters,
	selectSelectedSort
} from './product-list.selector';
import {
	selectPageType,
	selectUserStoreInfo,
	setUserStoreInfoFromCookie
} from '@state/data-access';

import {
	CRITEO_DIGITAL_SHELF_TALKER_POSITION,
	CriteoService
} from '@shared/data-access/product-recommendations';
import { DiscountEligibilityService } from '@shared/data-access/product';
import { Router } from '@angular/router';

@Injectable()
export class ProductListEffects {
	private readonly logger = inject(Logger);
	private readonly actions$ = inject(Actions);
	private readonly productCatalogService = inject(ProductCatalogService);
	private readonly discountEligibilityService = inject(DiscountEligibilityService);
	private readonly store = inject(Store);
	private readonly productListApiService = inject(ProductListApiService);
	private readonly productListUIService = inject(ProductListUIService);
	private readonly router = inject(Router);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly state = inject(TransferState);
	private readonly plpMlService = inject(ProductListMlService);
	private readonly plpSnackbarService = inject(ProductListSnackbarService);
	private readonly criteoService = inject(CriteoService);
	private readonly platformId = inject(PLATFORM_ID);

	/**
	 * @description
	 * When product list is updated, get the availability information and set data in state
	 */
	updateProductAvailability$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setProductList, setUserStoreInfoFromCookie, setCriteoProducts),
			withLatestFrom(
				this.store.select(selectProductVOs),
				this.store.select(selectCriteoProducts),
				this.store.select(selectUserStoreInfo)
			),
			switchMap(
				([, productVOs, criteoProducts, userSelectedStore]: [
					Action,
					ProductVO[],
					CriteoViewProductsResponse | null,
					HeaderStoreSelection
				]) => this.productAvailability(productVOs, criteoProducts, userSelectedStore)
			),
			switchMap((productAvailability: ProductAvailabilityCollection) =>
				of(setProductAvailability({ productAvailability }))
			)
		)
	);

	/**
	 * @description
	 * When product list is updated get promotion information and set data in state
	 */
	setPromoMessages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setProductList),
			withLatestFrom(this.store.select(selectProductVOs)),
			switchMap(([, productVOs]: [Action, ProductVO[]]) => this.productPromotion(productVOs)),
			switchMap((promo: ProductPromotionCollection | ProductPromotionResponse[]) =>
				of(
					setProductListPromo({
						productPromotion:
							this.discountEligibilityService.generatePromoMessages(promo)
					})
				)
			)
		)
	);

	/**
	 * @Important
	 * @description Listens for dispatched filtering and page number actions and updates product list with the new data
	 * Gets triggered on the following actions getting dispatched:
		setFiltering,
		setPageNumber,
		removeAllFilters,
		setPageSize,
		updateSelectedSort,
		setPageSizeAndPageNumber,
		setMlBypass,
		changeHeaderServiceStoreInfo,
		setUserStoreLocationAndZipCode,
		setHeaderServiceStoreInfo,
		setFallbackSearchParams
	 */
	updateProductList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshProductList),
			withLatestFrom(
				this.store.select(selectProductListCore),
				this.store.select(selectSelectedFilters),
				this.store.select(selectUserStoreInfo),
				this.store.select(selectFeatureFlags),
				this.store.select(selectProductListSEOAttributes),
				this.store.select(selectPageNumber),
				this.store.select(selectPageSize)
			),
			switchMap(
				([
					,
					plc,
					filters,
					selectedStoreInfo,
					featureFlags,
					seoAttributes,
					pageNumber,
					pageSize
				]: [
					Action,
					ProductListCore,
					Record<string, string[]>,
					HeaderStoreSelection,
					Record<string, boolean>,
					SEOAttributes,
					number,
					number
				]) => {
					if (featureFlags.isUsingCriteoSPAAdServer) {
						this.updateCriteoProducts(seoAttributes, filters, pageNumber, pageSize);
					}

					return this.updateProductList(
						plc,
						filters,
						selectedStoreInfo,
						featureFlags.showPinnedProducts,
						featureFlags.fulfillmentFacetsEnabled
					);
				}
			),
			withLatestFrom(
				this.store.select(selectRefinementUpdateObj),
				this.store.select(selectFallbackSearchParams)
			),
			switchMap(
				([productList, refinementUpdateObj, fallbackSearchParams]: [
					ProductList,
					RefinementUpdateObj,
					FallbackSearchParams
				]) => {
					if (!productList) {
						this.plpSnackbarService.openSnackbar(
							'Something went wrong. Please try your last selection again.'
						);
						// Return back to the fallback search params from the last successful action
						return of(
							setSkeletonLoader({ isSkeletonLoaderVisible: false }),
							setAllFilters({ selectedFilters: fallbackSearchParams?.filters }),
							setSelectedSort({ selectedSort: fallbackSearchParams?.selectedSort }),
							setPageSizeAndPageNumber({
								pageSize: fallbackSearchParams?.pageSize,
								pageNumber: fallbackSearchParams?.pageNumber
							})
						);
					}

					// Set a new ML search event parent id for this new search (post page-load)
					// i.e. filtering, sorting, page change, page size change, etc.
					this.plpMlService.generateNewMlSearchEventParentUUID();

					if (!refinementUpdateObj) {
						return of(setProductList({ productList }));
					}

					const refinementUpdateObjCopy = {
						...refinementUpdateObj,
						UpdateValue: { ...refinementUpdateObj.UpdateValue },
						['PLP_event_id']: this.plpMlService.getMlSearchEventParentUUID()
					};

					refinementUpdateObjCopy.UpdateValue.ProductCountTotal = productList?.totalCount;
					this.analyticsService.triggerAnalyticsEvent(
						'ResultsViewUpdate',
						refinementUpdateObjCopy
					);
					return of(
						setRefinementUpdateObj({ refinementUpdateObj: undefined }),
						setProductList({ productList })
					);
				}
			)
		)
	);

	/**
	 * @description Effect to handle setting page query parameters and navigating.
	 *
	 * This effect listens for actions related to pagination, filtering, and sorting,
	 * and updates the query parameters in the URL accordingly.
	 *
	 * Actions handled: setPageNumber, setPageSizeAndPageNumber, setFiltering, removeAllFilters, updateSelectedSort
	 *
	 * It uses the latest values from the store for page number, page size, filters, and selected sort,
	 * and calls the `setQueryParamsAndNavigate` method to update the URL.
	 *
	 * @effect
	 * @returns {Observable<null>} An observable that emits `null` and does not dispatch any actions.
	 */
	setPageQueryParam$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					setPageNumber,
					setPageSizeAndPageNumber,
					setFiltering,
					removeAllFilters,
					updateSelectedSort
				),
				withLatestFrom(
					this.store.select(selectPageNumber),
					this.store.select(selectPageSize),
					this.store.select(selectSelectedFilters),
					this.store.select(selectSelectedSort),
					this.store.select(selectProductListSEO),
					this.store.select(selectPageType)
				),
				switchMap(
					([, pageNumber, pageSize, filters, selectedSort, productListSEO, pageType]: [
						Action,
						number,
						number,
						Record<string, string[]>,
						{ label: string; value: number },
						ProductListSEO,
						any
					]) => {
						this.setQueryParamsAndNavigate(
							pageNumber,
							pageSize,
							filters,
							selectedSort,
							productListSEO,
							pageType
						);
						return of(null);
					}
				)
			),
		{ dispatch: false }
	);

	/**
	 * Effect to update the displayable product list.
	 *
	 * This effect listens for actions that set the product list, pinned content, or sponsored products.
	 * It then combines the latest values from the store for pinned content and sponsored products.
	 * If either of these values are missing, it waits for 500 milliseconds before proceeding.
	 *
	 * Once the necessary data is available, it then builds the displayable
	 * product list using these values.
	 *
	 * Finally, it dispatches an action to set the displayable product list data.
	 *
	 * @returns An observable of the action to set the displayable product list data.
	 */
	updateDisplayableProductList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setProductList, setPinnedContent, setPiqProducts, setCriteoProducts),
			withLatestFrom(
				this.store.select(selectPinnedContent),
				this.store.select(selectPIQProducts),
				this.store.select(selectCriteoProducts)
			),
			skipWhile(
				([, pinnedContent, piqProducts, criteoProducts]) =>
					isPlatformBrowser(this.platformId) &&
					(pinnedContent === null || (piqProducts === null && criteoProducts === null))
			),
			withLatestFrom(
				this.store.select(selectProductVOs),
				this.store.select(selectSearchVO),
				this.store.select(selectPinnedContent),
				this.store.select(selectPIQProducts),
				this.store.select(selectCriteoProducts),
				this.store.select(selectSelectedFilters),
				this.store.select(selectPageNumber),
				this.store.select(selectProductDetails),
				this.store.select(selectPageType),
				this.store.select(selectProductListSEO),
				this.store.select(selectFeatureFlags)
			),
			map(
				([
					[action],
					productVOs,
					searchVO,
					pinnedContent,
					piqProducts,
					criteoProducts,
					filters,
					pageNumber,
					productDetails,
					pageType,
					productSEO,
					featureFlags
				]) => {
					this.plpMlService.updateDisplayableProductListActionType.set(action.type);
					let sponsoredProducts;
					let sponsoredPinnedContent: CriteoPinnedContentSpot | null = null;
					let criteoGridOnLoadBeacon = '';
					if (criteoProducts) {
						if (featureFlags.isDigitalShelfTalkerEnabled) {
							const digitalShelfTalker = criteoProducts.placements.find(
								(p) => 'digitalShelfTalker' in p
							)?.digitalShelfTalker;
							if (digitalShelfTalker) {
								sponsoredPinnedContent =
									this.criteoService.transformToPinnedContent(
										digitalShelfTalker.rendering
									);
							}
						}
						const criteoInGrid = criteoProducts?.placements[1]?.inGrid[0];
						const criteoInGridProducts = criteoInGrid?.products;
						sponsoredProducts = criteoInGridProducts
							? this.criteoService.transformCriteoProducts(criteoInGridProducts)
							: null;
						criteoGridOnLoadBeacon = criteoInGrid?.OnLoadBeacon;
						if (
							productSEO?.attr?.SPONSORED_PRODUCTS?.value === 'N' &&
							sponsoredProducts?.length
						) {
							const allSponsoredProductsIDs = sponsoredProducts.map(
								(prod) => prod.product.ID
							);
							const sponProdIDsString = allSponsoredProductsIDs.join('|');
							this.criteoService.fireInGridOnLoadBeacon(
								criteoGridOnLoadBeacon,
								sponProdIDsString
							);
							sponsoredProducts = null;
						}
					} else {
						sponsoredProducts = piqProducts;
					}

					return this.buildDisplayableProductList(
						{
							productVOs,
							searchVO,
							pinnedContent,
							sponsoredProducts,
							filters,
							pageNumber,
							productDetails,
							pageType,
							sponsoredPinnedContent
						},
						featureFlags,
						criteoGridOnLoadBeacon,
						productSEO?.attr?.SPONSORED_PRODUCTS?.value
					);
				}
			),
			switchMap((dpld) =>
				of(setDisplayableProductListData({ displayableProductListData: dpld }))
			),
			tap(() => {
				if (
					this.plpMlService.updateDisplayableProductListActionType() !==
						setCriteoProducts.type ||
					this.plpMlService.plpListIsFirstRender()
				) {
					this.plpMlService.sendSearchEventData();
					this.plpMlService.plpListIsFirstRender.set(false);
				}
			})
		)
	);

	/**
	 * @description Sets the query parameters and navigates to the updated URL.
	 */
	private setQueryParamsAndNavigate(
		pageNumber: number,
		pageSize: number,
		filters: Record<string, string[]>,
		selectedSort: { label: string; value: number },
		productListSEO: ProductListSEO,
		pageType: any
	): void {
		const pageNumberQueryParam = pageNumber || null;
		const pageSizeQueryParam = pageSize === DEFAULT_PAGE_SIZE ? null : pageSize;
		const filtersString = Object.keys(filters).length
			? this.generateFilterString(filters)
			: null;
		const selectedSortQueryParam =
			selectedSort.value === this.getDefultSelectedSort(productListSEO, pageType)
				? null
				: selectedSort.value;

		this.router.navigate([], {
			queryParams: {
				pageNumber: pageNumberQueryParam,
				pageSize: pageSizeQueryParam,
				filterFacets: filtersString,
				selectedSort: selectedSortQueryParam
			},
			queryParamsHandling: 'merge'
		});
	}

	private getDefultSelectedSort(productListSEO: ProductListSEO, pageType: any): number {
		if (pageType === 'plp' && productListSEO?.longDescription) {
			return plpDefaultSortMap[productListSEO.longDescription];
		}
		return 0;
	}

	/**
	 * Checks the availability of a list of products in a specific store.
	 *
	 * @param productVOs - An array of ProductVO objects representing the products to check.
	 * @param criteoProducts - An array of Criteo products to include in the availability check.
	 * @param store - The store information including zipcode and store location.
	 * @returns An Observable that emits a ProductAvailabilityCollection, null, or an empty array in case of an error.
	 */
	private productAvailability(
		productVOs: ProductVO[],
		criteoProducts: CriteoViewProductsResponse | null,
		store: HeaderStoreSelection
	): Observable<ProductAvailabilityCollection | null | any[]> {
		if (isPlatformServer(this.platformId)) {
			return of(null);
		}

		const regularEcodes: string[] = productVOs?.length
			? productVOs
					.filter((item) => item?.parentPartnumber)
					.map((item) => item.parentPartnumber)
					.filter((ecode) => ecode)
			: [];

		const criteoEcodes: string[] = criteoProducts?.placements?.[1]?.inGrid?.[0]?.products
			?.length
			? criteoProducts.placements[1].inGrid[0].products
					.map((product) => product.ParentSKU)
					.filter((ecode) => ecode)
			: [];

		const allEcodes = [...new Set([...regularEcodes, ...criteoEcodes])];

		if (!allEcodes.length) {
			return of(null);
		}

		return this.productCatalogService
			.getAvailabilityData(
				allEcodes,
				true,
				store?.zipcode || store?.zip,
				store?.store || store?.location
			)
			.pipe(catchError(() => of([])));
	}

	private productPromotion(
		productVOs: ProductVO[]
	): Observable<ProductPromotionCollection | ProductPromotionResponse[] | null> {
		if (!isPlatformBrowser(this.platformId) || !productVOs?.length) {
			return of(null);
		}

		return this.discountEligibilityService.getPromoList$(productVOs);
	}

	/**
	 * @description set up params and make product list call
	 */
	private updateProductList(
		plc: ProductListCore,
		filters: Record<string, string[]>,
		selectedStoreInfo: HeaderStoreSelection,
		showPinnedProducts = true,
		fulfillmentFacetsEnabled = false,
		showSkeletonLoader = true
	): Observable<ProductList> {
		if (showSkeletonLoader) {
			this.store.dispatch(setSkeletonLoader({ isSkeletonLoaderVisible: true }));
		}

		//check if effect was triggered by a page change, and if so set the new page number for the call
		//otherwise reset the page number to zero so that we get the first page of filtered results
		const pageNumber = plc.pageNumber || 0;
		const pageSize = plc.pageSize || this.productListUIService.getDefaultProductListPageSize();
		const catId = plc.productListSEO?.catgroupId;

		let selectedStoreNumber = '';
		let zipcode = '';

		const includeFulfillmentFacets = fulfillmentFacetsEnabled;

		if (selectedStoreInfo) {
			selectedStoreNumber = selectedStoreInfo.location || selectedStoreInfo.store || '';
			zipcode = selectedStoreInfo.zipcode || selectedStoreInfo?.zip || '';
		}

		const params = this.productListApiService.setProductListParamDataForApi(
			catId || null,
			catId ? null : plc.productList?.searchVO?.searchTerm,
			pageNumber,
			filters,
			pageSize,
			includeFulfillmentFacets,
			selectedStoreNumber,
			zipcode
		);

		return this.productListApiService
			.getProductListDataFromApi$(params, 5, showPinnedProducts)
			.pipe(
				tap(() => {
					this.store.dispatch(setSkeletonLoader({ isSkeletonLoaderVisible: false }));
				}),
				catchError((error) => {
					this.logger.error(
						`Search API error for search term ${
							catId || plc.productList?.searchVO?.searchTerm
						} with the following error: ${JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name'])}`
					);
					return of(null);
				})
			);
	}

	private updateCriteoProducts(
		seoAttributes: SEOAttributes,
		filters: Record<string, string[]>,
		pageNumber: number,
		pageSize: number
	): void {
		const pageType = seoAttributes
			? ProductTypes.VIEW_CATEGORY
			: ProductTypes.VIEW_SEARCH_RESULT;
		const categoryValue: string | undefined =
			this.router.routerState?.snapshot?.root?.queryParams?.searchTerm ??
			seoAttributes?.BREADCRUMB?.value;

		if (!categoryValue) {
			return;
		}

		this.productListUIService.setFilteredCriteoProductsToState(
			{
				productType: pageType,
				category: categoryValue,
				pageNumber: pageNumber,
				listSize: pageSize
			},
			filters
		);
	}

	private generateFilterString(filters): string | null {
		const keys = Object.keys(filters);
		if (keys.length === 0) {
			return null;
		}

		// Build the string we will use to add to the URL
		let filtersString = '';
		keys.forEach((key, index) => {
			const filterList = filters[key].join();
			const separator = index === keys.length - 1 ? '' : ';';
			filtersString += `${key}:${filterList}${separator}`;
		});

		return encodeURIComponent(filtersString);
	}

	private buildDisplayableProductList(
		{
			productVOs,
			searchVO,
			pinnedContent,
			sponsoredProducts,
			filters,
			pageNumber,
			productDetails,
			pageType,
			sponsoredPinnedContent
		}: DisplayableProductListParams,
		featureFlags: Record<string, boolean>,
		gridLoadBeacon?: string,
		sponsoredProductsSEO?: string
	): DisplayableProductListData {
		if (!productVOs?.length) {
			// If we are on a SRLP and we are not going to be redirecting to a landing page,
			// we need to set the loading state to false
			this.handleEmptyProductList(searchVO, pageType);
			return [];
		}

		const productVOsCopy = [...productVOs];
		const sponsoredProductsCopy = Array.isArray(sponsoredProducts)
			? [...sponsoredProducts]
			: [];
		const skippedSponsoredProductsIDs = [];
		const dpld: DisplayableProductListData = [];

		const productPartNumbers: string[] = [];
		const productECodes: string[] = [];
		let productCardCount = 0;

		let index = 0;
		while (productVOsCopy.length) {
			if (pinnedContent?.[index] && pageNumber === 0) {
				// Add a pinned content item
				dpld.push(pinnedContent[index]);
			} else if (
				this.criteoService.isSponsoredProduct(
					sponsoredProductsCopy,
					productCardCount,
					pageNumber,
					filters,
					featureFlags,
					pinnedContent
				)
			) {
				const currentProduct = sponsoredProductsCopy.shift();
				skippedSponsoredProductsIDs.push(currentProduct.product.ID);
				// If sponsored product is already in the display list, don't use a sponsored slot on it.
				if (!productECodes.includes(currentProduct.product_id)) {
					productCardCount += 1;
					dpld.push(currentProduct);
					let dupes: number[] = this.dedupeProductVOs(productVOsCopy, currentProduct);
					dupes?.forEach((index) => {
						productVOsCopy.splice(index, 1);
					});
					skippedSponsoredProductsIDs.pop();
				}
			} else if (
				// If the item is an invalid or duplicate product, remove it from the array
				productVOsCopy.length &&
				(!this.isValidProduct(productVOsCopy[0], productDetails) ||
					productPartNumbers.includes(productVOsCopy[0].partnumber))
			) {
				productVOsCopy.shift();
			} else if (!productPartNumbers.includes(productVOsCopy[0].partnumber)) {
				productCardCount += 1;
				const currentProduct = productVOsCopy.shift();

				productPartNumbers.push(currentProduct.partnumber);
				productECodes.push(currentProduct.parentPartnumber);

				const currentProductWithDetails = this.attachProductDetailsToProductList(
					currentProduct,
					productDetails
				);

				dpld.push(currentProductWithDetails);
			}
			index++;
		}

		if (sponsoredPinnedContent) {
			dpld.splice(CRITEO_DIGITAL_SHELF_TALKER_POSITION, 0, sponsoredPinnedContent);
		}

		// Logic for firing inGrid onLoadBeacon if we are showing Sponsored Products
		this.criteoService.checkOnLoadBeaconFiring(
			sponsoredProductsSEO,
			gridLoadBeacon,
			sponsoredProductsCopy,
			skippedSponsoredProductsIDs
		);
		this.checkPlatformServer(dpld);
		this.dispatchLoadingIfNeeded(pageType);

		return dpld;
	}

	private dedupeProductVOs(
		productVOs: ProductVO[],
		sponsoredProduct: PlpInGridSponsoredProduct
	): number[] {
		const dupes: number[] = [];
		const length = productVOs.length < 5 ? productVOs.length - 1 : 4;
		for (let i = length; i >= 0; i--) {
			if (productVOs[i].parentPartnumber === sponsoredProduct.product_id) {
				dupes.push(i);
			}
		}
		return dupes;
	}

	private handleEmptyProductList(searchVO: SearchVO, pageType: string): void {
		if (!searchVO?.landingPageURL) {
			this.dispatchLoadingIfNeeded(pageType);
		}
	}

	private checkPlatformServer(dpld: DisplayableProductListData): void {
		if (isPlatformServer(this.platformId)) {
			const ELEMENT_KEY = makeStateKey<any>('DISPLAYABLE_PRODUCT_LIST_DATA');
			this.state.set(ELEMENT_KEY, dpld);
		}
	}

	private dispatchLoadingIfNeeded(pageType: string): void {
		if (pageType === 'srlp') {
			this.store.dispatch(setIsProductListPageLoading({ isProductListPageLoading: false }));
		}
	}

	private attachProductDetailsToProductList(
		product: ProductVO,
		productDetails: Record<string, ProductDetail>
	) {
		const id = product.parentCatentryId ? product.parentCatentryId : product.catentryId;
		const { priceIndicator, mapPriceIndicator, prices, skuAttributes } = productDetails[id];

		return {
			...product,
			priceIndicator,
			mapPriceIndicator,
			prices,
			skuAttributes
		};
	}

	/**  @description throw console warns to indicate why a product is not displaying in the grid*/
	private isValidProduct(
		product: ProductVO,
		productDetails: Record<string, ProductDetail>
	): boolean {
		if (!product.parentCatentryId && !product.catentryId) {
			console.warn('Product is Missing:  parentCatentryId and catentryId', product);
			return false;
		} else if (!product.partnumber || !product.parentPartnumber) {
			console.warn(
				`Product is Missing: ${!product.partnumber ? 'partnumber' : ''} ${
					!product.parentPartnumber ? 'parentPartnumber' : ''
				}`,
				product
			);
			return false;
		} else if (!product.name) {
			console.warn('Product is Missing Name', product);
			return false;
		}
		const id = product.parentCatentryId ? product.parentCatentryId : product.catentryId;
		const productDetail = productDetails[id];
		if (
			productDetail?.mapPriceIndicator === undefined ||
			productDetail?.priceIndicator === undefined ||
			productDetail?.prices.maxlistprice === undefined ||
			productDetail?.prices.minlistprice === undefined ||
			productDetail?.prices.maxofferprice === undefined ||
			productDetail?.prices.minofferprice === undefined
		) {
			console.warn(
				'ProductDetail does not have a required field and will not be displayed. Missing one of: productDetail, mapPriceIndicator, priceIndicator, prices',
				product
			);
			return false;
		}
		return true;
	}
}
